import React, { useState, useMemo, useEffect } from 'react';
import { useUser, useAuth, UserButton } from '@clerk/clerk-react';
import ReactMarkdown from 'react-markdown';
import { JobTrackingStatus } from '../api/jobTracking';
import { StatusDropdown, getStatusBadgeClasses } from '../components/StatusDropdown';
import { loadJobs, isStaticMode } from '../api/staticData';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
const SERVER_URL = process.env.REACT_APP_SERVER_URL || 'http://localhost:3001';

interface JobTags {
  language?: string[];
  specialization?: string[];
  industry?: string[];
  infrastructure?: string[];
  companyStage?: string[];
  experienceLevel?: string[];
}

interface Job {
  id: string;
  title: string;
  description: string;
  location: string;
  locationType: string;
  salaryMin?: number;
  salaryMax?: number;
  salaryCurrency: string;
  equityOffered: boolean;
  experienceLevel?: string;
  visaSponsorship: boolean;
  techStack: string[];
  tags?: JobTags;
  sourceUrl: string;
  sourceType: string;
  applyUrl?: string;
  postedAt: string;
  company: {
    id: string;
    name: string;
    slug: string;
    logoUrl?: string;
    logoPath?: string;
    averageRating?: number;
    reviewCount: number;
  };
  trackingStatus?: JobTrackingStatus | null;
  trackingId?: string;
}

type SortOption = 'date' | 'status' | 'company';
type GroupOption = 'none' | 'remote' | 'status';

// Tag category colors
const TAG_COLORS: Record<string, string> = {
  language: 'bg-blue-100 text-blue-800',
  specialization: 'bg-purple-100 text-purple-800',
  industry: 'bg-green-100 text-green-800',
  infrastructure: 'bg-orange-100 text-orange-800',
  companyStage: 'bg-pink-100 text-pink-800',
  experienceLevel: 'bg-yellow-100 text-yellow-800',
};

// Get one tag per category for display (max 6 tags)
const getDisplayTags = (tags?: JobTags): Array<{ category: string; value: string }> => {
  if (!tags) return [];
  const displayTags: Array<{ category: string; value: string }> = [];
  const categories: (keyof JobTags)[] = ['language', 'specialization', 'industry', 'infrastructure', 'companyStage', 'experienceLevel'];

  for (const category of categories) {
    const values = tags[category];
    if (values && values.length > 0) {
      displayTags.push({ category, value: values[0] });
    }
  }
  return displayTags;
};

const Dashboard: React.FC = () => {
  const { user } = useUser();
  const { getToken } = useAuth();

  const [jobs, setJobs] = useState<Job[]>([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 25,
    total: 0,
    totalPages: 0,
  });
  const [isLoading, setIsLoading] = useState(true);

  // Load persisted state from localStorage
  const getPersistedState = () => {
    try {
      const stored = localStorage.getItem('dashboardState');
      if (stored) {
        return JSON.parse(stored);
      }
    } catch {}
    return {};
  };

  const persistedState = getPersistedState();

  const [sortBy, setSortBy] = useState<SortOption>(persistedState.sortBy || 'date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>(persistedState.sortOrder || 'desc');
  const [groupBy, setGroupBy] = useState<GroupOption>(persistedState.groupBy || 'none');
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const [showGroupDropdown, setShowGroupDropdown] = useState(false);
  const [expandedJobIds, setExpandedJobIds] = useState<Set<string>>(new Set());
  const [updatingStatusId, setUpdatingStatusId] = useState<string | null>(null);
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);
  const [tagFilters, setTagFilters] = useState<Record<string, string[]>>(persistedState.tagFilters || {});

  // Persist state changes to localStorage
  useEffect(() => {
    localStorage.setItem('dashboardState', JSON.stringify({
      sortBy,
      sortOrder,
      groupBy,
      tagFilters,
    }));
  }, [sortBy, sortOrder, groupBy, tagFilters]);

  // Load tracking from localStorage (for static mode)
  const getLocalTracking = (): Map<string, { status: JobTrackingStatus; id: string }> => {
    try {
      const stored = localStorage.getItem('jobTracking');
      if (stored) {
        const data = JSON.parse(stored);
        return new Map(Object.entries(data));
      }
    } catch {}
    return new Map();
  };

  const saveLocalTracking = (trackingMap: Map<string, { status: JobTrackingStatus; id: string }>) => {
    localStorage.setItem('jobTracking', JSON.stringify(Object.fromEntries(trackingMap)));
  };

  // Fetch all published jobs with user's tracking status
  const fetchJobs = async () => {
    setIsLoading(true);
    try {
      const token = await getToken();
      let result;
      let trackingMap = new Map();

      if (isStaticMode()) {
        // Production: load from static JSON
        result = await loadJobs();
        trackingMap = getLocalTracking();
      } else {
        // Development: use API (fetch all jobs, max 100)
        const response = await fetch(`${API_URL}/jobs?limit=100`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch jobs');
        }

        result = await response.json();

        // Fetch user's tracking records from API
        const trackingResponse = await fetch(`${API_URL}/tracked-jobs?limit=1000`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (trackingResponse.ok) {
          const trackingData = await trackingResponse.json();
          trackingMap = new Map(
            trackingData.data.map((t: any) => [t.jobId, { status: t.status, id: t.id }])
          );
        }
      }

      // Add tracking info to jobs
      const jobsWithTracking = result.data.map((job: Job) => ({
        ...job,
        trackingStatus: trackingMap.get(job.id)?.status || null,
        trackingId: trackingMap.get(job.id)?.id || null,
      }));

      setJobs(jobsWithTracking);
      setPagination({
        page: 1,
        limit: result.total,
        total: result.total,
        totalPages: 1,
      });
    } catch (error) {
      console.error('Failed to fetch jobs:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch jobs on mount
  useEffect(() => {
    fetchJobs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle sort change
  const handleSortChange = async (newSortBy: SortOption) => {
    setSortBy(newSortBy);
    setShowSortDropdown(false);
  };

  // Handle status change (create or update tracking)
  const handleStatusChange = async (jobId: string, newStatus: JobTrackingStatus, currentTrackingId?: string) => {
    setUpdatingStatusId(jobId);
    try {
      if (isStaticMode()) {
        // Production: use localStorage
        const trackingMap = getLocalTracking();
        trackingMap.set(jobId, { status: newStatus, id: jobId });
        saveLocalTracking(trackingMap);

        // Update local state
        setJobs(prev => prev.map(job =>
          job.id === jobId
            ? { ...job, trackingStatus: newStatus, trackingId: jobId }
            : job
        ));
      } else {
        // Development: use API
        const token = await getToken();

        if (currentTrackingId) {
          // Update existing tracking
          await fetch(`${API_URL}/tracked-jobs/${currentTrackingId}`, {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ status: newStatus }),
          });
        } else {
          // Create new tracking
          const response = await fetch(`${API_URL}/tracked-jobs`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ jobId, status: newStatus }),
          });

          if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error || 'Failed to track job');
          }
        }

        // Refresh jobs list
        await fetchJobs();
      }
    } catch (error) {
      console.error('Failed to update status:', error);
      alert('Failed to update status. Please try again.');
    } finally {
      setUpdatingStatusId(null);
    }
  };

  // Handle removing tracking
  const handleRemoveTracking = async (trackingId: string, jobTitle: string) => {
    const confirmed = window.confirm(
      `Remove tracking for "${jobTitle}"? This will clear your application status for this job.`
    );

    if (!confirmed) return;

    try {
      if (isStaticMode()) {
        // Production: use localStorage
        const trackingMap = getLocalTracking();
        trackingMap.delete(trackingId);
        saveLocalTracking(trackingMap);

        // Update local state
        setJobs(prev => prev.map(job =>
          job.trackingId === trackingId
            ? { ...job, trackingStatus: null, trackingId: undefined }
            : job
        ));
      } else {
        // Development: use API
        const token = await getToken();
        await fetch(`${API_URL}/tracked-jobs/${trackingId}`, {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        // Refresh jobs list
        await fetchJobs();
      }
    } catch (error) {
      console.error('Failed to remove tracking:', error);
      alert('Failed to remove tracking. Please try again.');
    }
  };

  // Get all available tags from jobs for filter options
  const availableTags = useMemo(() => {
    const tags: Record<string, Set<string>> = {
      language: new Set(),
      specialization: new Set(),
      industry: new Set(),
      infrastructure: new Set(),
      companyStage: new Set(),
      experienceLevel: new Set(),
    };

    jobs.forEach((job) => {
      if (job.tags) {
        Object.entries(job.tags).forEach(([category, values]) => {
          if (values && tags[category]) {
            values.forEach((v: string) => tags[category].add(v));
          }
        });
      }
    });

    return Object.fromEntries(
      Object.entries(tags).map(([k, v]) => [k, Array.from(v).sort()])
    ) as Record<string, string[]>;
  }, [jobs]);

  // Toggle a tag filter
  const toggleTagFilter = (category: string, value: string) => {
    setTagFilters((prev) => {
      const current = prev[category] || [];
      const newValues = current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value];
      return { ...prev, [category]: newValues };
    });
  };

  // Clear all filters
  const clearAllFilters = () => {
    setTagFilters({});
  };

  // Check if any filters are active
  const hasActiveFilters = Object.values(tagFilters).some((v) => v.length > 0);

  // Group and sort jobs
  const groupedJobs = useMemo(() => {
    let filteredJobs = [...jobs];

    // Apply tag filters
    if (hasActiveFilters) {
      filteredJobs = filteredJobs.filter((job) => {
        if (!job.tags) return false;
        return Object.entries(tagFilters).every(([category, selectedValues]) => {
          if (selectedValues.length === 0) return true;
          const jobTagsInCategory = job.tags?.[category as keyof JobTags] || [];
          return selectedValues.some((v) => jobTagsInCategory.includes(v));
        });
      });
    }

    let sortedJobs = filteredJobs;

    // Client-side sorting
    if (sortBy === 'company') {
      sortedJobs.sort((a, b) => a.company.name.localeCompare(b.company.name));
    } else if (sortBy === 'date') {
      sortedJobs.sort((a, b) => new Date(b.postedAt).getTime() - new Date(a.postedAt).getTime());
    } else if (sortBy === 'status') {
      const statusOrder: Record<string, number> = {
        ACCEPTED: 0,
        OFFER: 1,
        INTERVIEW: 2,
        APPLIED: 3,
        SAVED: 4,
        REJECTED: 5,
        WITHDRAWN: 6,
        '': 99, // Not tracked
      };
      sortedJobs.sort((a, b) => {
        const aStatus = a.trackingStatus || '';
        const bStatus = b.trackingStatus || '';
        return (statusOrder[aStatus] ?? 99) - (statusOrder[bStatus] ?? 99);
      });
    }

    if (groupBy === 'none') {
      return [{ groupName: 'All Jobs', jobs: sortedJobs }];
    }

    const groups: Record<string, typeof sortedJobs> = {};

    sortedJobs.forEach((job) => {
      let groupKey: string;

      if (groupBy === 'remote') {
        const isRemote =
          job.locationType === 'REMOTE' ||
          job.location?.toLowerCase().includes('remote');
        groupKey = isRemote ? 'Remote' : 'On-site/Hybrid';
      } else if (groupBy === 'status') {
        groupKey = job.trackingStatus || 'Not Tracked';
      } else {
        groupKey = 'All Jobs';
      }

      if (!groups[groupKey]) {
        groups[groupKey] = [];
      }
      groups[groupKey].push(job);
    });

    // Convert to array and sort groups
    return Object.entries(groups)
      .sort(([keyA], [keyB]) => {
        if (groupBy === 'remote') {
          return keyA === 'Remote' ? -1 : keyB === 'Remote' ? 1 : 0;
        }
        if (groupBy === 'status') {
          const statusOrder: Record<string, number> = {
            ACCEPTED: 0,
            OFFER: 1,
            INTERVIEW: 2,
            APPLIED: 3,
            SAVED: 4,
            REJECTED: 5,
            WITHDRAWN: 6,
            'Not Tracked': 99,
          };
          return (statusOrder[keyA] ?? 99) - (statusOrder[keyB] ?? 99);
        }
        return keyA.localeCompare(keyB);
      })
      .map(([groupName, jobs]) => ({ groupName, jobs }));
  }, [jobs, groupBy, sortBy, tagFilters, hasActiveFilters]);

  return (
    <div className="min-h-screen bg-darkbrown-950 relative">
      {/* Navigation */}
      <nav className="bg-darkbrown-900 border-b border-cream-200/10 sticky top-0 z-40 backdrop-blur-sm bg-darkbrown-900/80">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <a href="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
              <span className="text-2xl">🏰</span>
              <span className="font-display text-xl font-bold text-amber-400">Guildhall.dev</span>
            </a>
            <div className="flex items-center gap-4">
              <span className="text-cream-200">
                Welcome, {user?.firstName || 'User'}
              </span>
              <UserButton
                appearance={{
                  elements: {
                    avatarBox: "w-10 h-10"
                  }
                }}
              />
            </div>
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-display font-bold text-cream-100 mb-2">
            My Job Dashboard
          </h1>
          <p className="text-cream-200/70">
            Track your job applications and stay organized
          </p>
        </div>

        {/* Controls */}
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-cream-200/70 text-sm">
              {pagination.total} job{pagination.total !== 1 ? 's' : ''} tracked
            </span>
          </div>

          <div className="flex items-center gap-2">
            {/* Group By Dropdown */}
            <div className="relative">
              <button
                className="px-4 py-2 bg-darkbrown-800 border border-cream-200/10 text-cream-200 rounded-md hover:bg-darkbrown-700 transition-colors flex items-center gap-2"
                onClick={() => setShowGroupDropdown(!showGroupDropdown)}
              >
                Group by:{' '}
                {groupBy === 'none'
                  ? 'None'
                  : groupBy === 'remote'
                  ? 'Remote'
                  : 'Status'}
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>

              {showGroupDropdown && (
                <div className="absolute right-0 mt-2 w-48 bg-darkbrown-900/95 backdrop-blur-sm border border-cream-200/10 rounded-lg shadow-xl z-10">
                  <button
                    className={`w-full text-left px-4 py-2 text-sm hover:bg-darkbrown-800 ${
                      groupBy === 'none'
                        ? 'text-cream-100 bg-darkbrown-800'
                        : 'text-cream-200'
                    }`}
                    onClick={() => {
                      setGroupBy('none');
                      setShowGroupDropdown(false);
                    }}
                  >
                    None
                  </button>
                  <button
                    className={`w-full text-left px-4 py-2 text-sm hover:bg-darkbrown-800 ${
                      groupBy === 'remote'
                        ? 'text-cream-100 bg-darkbrown-800'
                        : 'text-cream-200'
                    }`}
                    onClick={() => {
                      setGroupBy('remote');
                      setShowGroupDropdown(false);
                    }}
                  >
                    Remote Jobs
                  </button>
                  <button
                    className={`w-full text-left px-4 py-2 text-sm hover:bg-darkbrown-800 rounded-b-lg ${
                      groupBy === 'status'
                        ? 'text-cream-100 bg-darkbrown-800'
                        : 'text-cream-200'
                    }`}
                    onClick={() => {
                      setGroupBy('status');
                      setShowGroupDropdown(false);
                    }}
                  >
                    Application Status
                  </button>
                </div>
              )}
            </div>

            {/* Sort Dropdown */}
            <div className="relative">
              <button
                className="px-4 py-2 bg-darkbrown-800 border border-cream-200/10 text-cream-200 rounded-md hover:bg-darkbrown-700 transition-colors flex items-center gap-2"
                onClick={() => setShowSortDropdown(!showSortDropdown)}
              >
                Sort by:{' '}
                {sortBy === 'date'
                  ? 'Date'
                  : sortBy === 'status'
                  ? 'Status'
                  : 'Company'}
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>

              {showSortDropdown && (
                <div className="absolute right-0 mt-2 w-48 bg-darkbrown-900/95 backdrop-blur-sm border border-cream-200/10 rounded-lg shadow-xl z-10">
                  <button
                    className={`w-full text-left px-4 py-2 text-sm hover:bg-darkbrown-800 ${
                      sortBy === 'date'
                        ? 'text-cream-100 bg-darkbrown-800'
                        : 'text-cream-200'
                    }`}
                    onClick={() => handleSortChange('date')}
                  >
                    Date Added
                  </button>
                  <button
                    className={`w-full text-left px-4 py-2 text-sm hover:bg-darkbrown-800 ${
                      sortBy === 'company'
                        ? 'text-cream-100 bg-darkbrown-800'
                        : 'text-cream-200'
                    }`}
                    onClick={() => handleSortChange('company')}
                  >
                    Company (A-Z)
                  </button>
                  <button
                    className={`w-full text-left px-4 py-2 text-sm hover:bg-darkbrown-800 rounded-b-lg ${
                      sortBy === 'status'
                        ? 'text-cream-100 bg-darkbrown-800'
                        : 'text-cream-200'
                    }`}
                    onClick={() => handleSortChange('status')}
                  >
                    Status
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Tag Filters */}
        {Object.values(availableTags).some((t) => t.length > 0) && (
          <div className="mb-6 p-4 bg-darkbrown-900 rounded-lg border border-cream-200/10">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-medium text-cream-200">Filter by Tags</h3>
              {hasActiveFilters && (
                <button
                  onClick={clearAllFilters}
                  className="text-xs text-amber-400 hover:text-amber-300"
                >
                  Clear all
                </button>
              )}
            </div>
            <div className="space-y-3">
              {Object.entries(availableTags).map(([category, values]) => {
                if (values.length === 0) return null;
                const categoryLabels: Record<string, string> = {
                  language: 'Language',
                  specialization: 'Role',
                  industry: 'Industry',
                  infrastructure: 'Infrastructure',
                  companyStage: 'Company',
                  experienceLevel: 'Level',
                };
                return (
                  <div key={category} className="flex flex-wrap items-center gap-2">
                    <span className="text-xs text-cream-200/50 w-20">{categoryLabels[category]}:</span>
                    {values.map((value) => {
                      const isSelected = (tagFilters[category] || []).includes(value);
                      return (
                        <button
                          key={value}
                          onClick={() => toggleTagFilter(category, value)}
                          className={`px-2 py-0.5 rounded-full text-xs font-medium transition-all ${
                            isSelected
                              ? TAG_COLORS[category]
                              : 'bg-cream-200/10 text-cream-200/70 hover:bg-cream-200/20'
                          }`}
                        >
                          {value}
                        </button>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Jobs List */}
        {isLoading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-cream-100"></div>
            <p className="text-cream-200/70 mt-4">Loading jobs...</p>
          </div>
        ) : jobs.length === 0 ? (
          <div className="text-center py-16 bg-darkbrown-900 rounded-lg border border-cream-200/10">
            <svg
              className="w-16 h-16 mx-auto text-cream-200/30 mb-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
              />
            </svg>
            <p className="text-xl text-cream-200 mb-2">No jobs available yet</p>
            <p className="text-cream-200/70">
              Check back soon for new job postings!
            </p>
          </div>
        ) : (
          <div className="space-y-8">
            {groupedJobs.map((group, groupIndex) => (
              <div key={groupIndex}>
                {/* Group Header */}
                {groupBy !== 'none' && (
                  <div className="mb-4">
                    <h3 className="text-lg font-semibold flex items-center gap-2">
                      <span
                        className={`${
                          groupBy === 'status'
                            ? getStatusBadgeClasses(group.groupName as JobTrackingStatus).split(' ')[1] ||
                              'text-cream-100'
                            : 'text-cream-100'
                        }`}
                      >
                        {group.groupName}
                      </span>
                      <span
                        className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                          groupBy === 'status'
                            ? getStatusBadgeClasses(group.groupName as JobTrackingStatus)
                            : 'bg-cream-100/10 text-cream-200'
                        }`}
                      >
                        {group.jobs.length}
                      </span>
                    </h3>
                    <div className="mt-2 h-px bg-gradient-to-r from-cream-200/20 to-transparent" />
                  </div>
                )}

                {/* Jobs in this group */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {group.jobs.map((job) => {
                    const isExpanded = expandedJobIds.has(job.id);
                    const hasOpenDropdown = openDropdownId === job.id;

                    const toggleExpanded = () => {
                      setExpandedJobIds(prev => {
                        const newSet = new Set(prev);
                        if (newSet.has(job.id)) {
                          newSet.delete(job.id);
                        } else {
                          newSet.add(job.id);
                        }
                        return newSet;
                      });
                    };

                    return (
                      <div
                        key={job.id}
                        className={`bg-cream-100 border border-cream-200 rounded-lg p-4 hover:border-gray-300 hover:shadow-md shadow-sm transition-all cursor-pointer ${
                          hasOpenDropdown ? 'z-50 relative' : ''
                        }`}
                        onClick={toggleExpanded}
                      >
                        <div className="flex justify-between items-stretch">
                          <div className="flex-1">
                            <div className="flex items-center gap-4 mb-3">
                              {/* Company Logo */}
                              {(job.company.logoPath || job.company.logoUrl) ? (
                                <img
                                  src={job.company.logoPath
                                    ? `${SERVER_URL}${job.company.logoPath}`
                                    : job.company.logoUrl}
                                  alt={`${job.company.name} logo`}
                                  className="w-16 h-16 rounded-md object-contain bg-cream-100 flex-shrink-0"
                                  onError={(e) => {
                                    (e.target as HTMLImageElement).style.display = 'none';
                                  }}
                                />
                              ) : (
                                <div className="w-16 h-16 rounded-md bg-gray-200 flex items-center justify-center flex-shrink-0">
                                  <span className="text-gray-600 text-xl font-semibold">
                                    {job.company.name.charAt(0).toUpperCase()}
                                  </span>
                                </div>
                              )}
                              <div>
                                <h3 className="text-xl font-semibold text-gray-900">
                                  {job.title}
                                </h3>
                                <div className="flex items-center gap-3 text-sm text-gray-600">
                                  <span>{job.company.name}</span>
                                  <span className="flex items-center gap-1">
                                    📍 {job.location}
                                  </span>
                                </div>
                                {/* Tags */}
                                {job.tags && getDisplayTags(job.tags).length > 0 && (
                                  <div className="flex flex-wrap gap-1.5 mt-2">
                                    {getDisplayTags(job.tags).map(({ category, value }) => (
                                      <span
                                        key={`${category}-${value}`}
                                        className={`px-2 py-0.5 rounded-full text-xs font-medium ${TAG_COLORS[category]}`}
                                      >
                                        {value}
                                      </span>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-4 text-sm text-gray-600">
                              {job.trackingStatus && (
                                <>
                                  <StatusDropdown
                                    currentStatus={job.trackingStatus}
                                    onStatusChange={(newStatus) =>
                                      handleStatusChange(job.id, newStatus, job.trackingId)
                                    }
                                    isUpdating={updatingStatusId === job.id}
                                    onOpenChange={(isOpen) =>
                                      setOpenDropdownId(isOpen ? job.id : null)
                                    }
                                  />
                                  {job.trackingId && (
                                    <button
                                      className="text-xs text-red-400 hover:text-red-300"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleRemoveTracking(job.trackingId!, job.title);
                                      }}
                                    >
                                      Clear Status
                                    </button>
                                  )}
                                </>
                              )}
                              {job.salaryMin && job.salaryMax && (
                                <span className="flex items-center gap-1">
                                  💰 ${job.salaryMin.toLocaleString()} - $
                                  {job.salaryMax.toLocaleString()}
                                </span>
                              )}
                            </div>
                          </div>

                          <div className="flex flex-col items-end justify-between ml-4 self-stretch">
                            {!job.trackingStatus && (
                              <button
                                className="px-3 py-1.5 rounded-md text-sm font-medium bg-amber-500 border border-amber-600 text-white hover:bg-amber-600 transition-all"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleStatusChange(job.id, JobTrackingStatus.SAVED);
                                }}
                                disabled={updatingStatusId === job.id}
                              >
                                {updatingStatusId === job.id ? '...' : '+ Track Job'}
                              </button>
                            )}
                            <span className="flex items-center gap-1 text-xs text-gray-500 whitespace-nowrap pb-2 pr-1">
                              Posted: {new Date(job.postedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                              <span className={`text-gray-400 text-lg transition-transform ${isExpanded ? 'rotate-90' : ''}`}>
                                »
                              </span>
                            </span>
                          </div>
                        </div>

                        {isExpanded && (
                          <div className="mt-4 pt-4 border-t border-gray-200">
                            <div className="mb-4">
                              <div className="text-sm text-gray-700 max-h-96 overflow-y-auto bg-cream-100 border border-gray-300 rounded-lg p-4 prose prose-sm prose-gray max-w-none">
                                <ReactMarkdown
                                  components={{
                                    h1: ({node, ...props}) => <h1 className="text-xl font-bold mt-4 mb-2 text-gray-900" {...props} />,
                                    h2: ({node, ...props}) => <h2 className="text-lg font-bold mt-3 mb-2 text-gray-900" {...props} />,
                                    h3: ({node, ...props}) => <h3 className="text-base font-semibold mt-2 mb-1 text-gray-900" {...props} />,
                                    p: ({node, ...props}) => <p className="mb-2" {...props} />,
                                    ul: ({node, ...props}) => <ul className="list-disc list-inside mb-2 space-y-1" {...props} />,
                                    ol: ({node, ...props}) => <ol className="list-decimal list-inside mb-2 space-y-1" {...props} />,
                                    li: ({node, ...props}) => <li className="ml-2" {...props} />,
                                    strong: ({node, ...props}) => <strong className="font-semibold text-gray-900" {...props} />,
                                    em: ({node, ...props}) => <em className="italic" {...props} />,
                                    a: ({node, ...props}) => <a className="text-blue-600 hover:underline" {...props} />,
                                    code: ({node, ...props}) => <code className="bg-gray-200 px-1 py-0.5 rounded text-xs font-mono" {...props} />,
                                    pre: ({node, ...props}) => <pre className="bg-gray-800 text-gray-100 p-2 rounded overflow-x-auto text-xs" {...props} />,
                                    blockquote: ({node, ...props}) => <blockquote className="border-l-4 border-gray-300 pl-4 italic my-2" {...props} />,
                                  }}
                                >
                                  {job.description}
                                </ReactMarkdown>
                              </div>
                            </div>

                            {job.techStack && job.techStack.length > 0 && (
                              <div className="mb-4">
                                <h4 className="text-sm font-semibold text-gray-800 mb-2">
                                  Tech Stack
                                </h4>
                                <div className="flex flex-wrap gap-2">
                                  {job.techStack.map((tech) => (
                                    <span
                                      key={tech}
                                      className="px-3 py-1 bg-gray-200 text-gray-800 rounded-full text-sm"
                                    >
                                      {tech}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}

                            <div className="flex items-center gap-4 text-sm text-gray-600">
                              <span>📮 Source: {job.sourceType}</span>
                              <a
                                href={job.sourceUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-gray-900 hover:underline"
                                onClick={(e) => e.stopPropagation()}
                              >
                                View Original Post →
                              </a>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}

          </div>
        )}
      </div>

      <footer className="border-t border-oak-600/50 py-8 px-6 mt-auto">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <span className="text-xl">🏰</span>
              <span className="font-display font-bold text-amber-400">Guildhall.dev</span>
            </div>
            <div className="text-sm text-cream-200/60">
              contact: admin@guildhall.dev
            </div>
            <div className="text-sm text-cream-200/40">
              © 2025 Guildhall.dev
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Dashboard;
